/*
 * The chat frontend module (modules.md §8). Everything the shell knows about chat is in this
 * object: its routes, its reducer for `chat.*` events, what it puts into core's slots, what it
 * offers core's own surfaces, how it binds to the running client (`bind.ts`), its translation
 * namespace and what it does with its share of the bootstrap. `web/src/modules.ts` is the only file
 * that names it, and switching chat off is deleting that one line.
 *
 * Three of those arrive as chunks of their own: the conversation screen behind `routes`, the copy
 * behind `i18n` and the channels part of Administration's *Open to AI chats*, which only an
 * administrator who opens that page fetches. The router awaits the first two before the first render (`routes/router.tsx`), so a screen
 * never draws a key it has no text for and a conversation never mounts behind a spinner; what the
 * split buys is an entry chunk that no longer carries them. What stays static is what a core frame
 * draws from the first paint on — the sidebar rows, the details views, the home section — and the
 * reducer, which the first event needs. A value import of `./i18n/en.ts` or `./routes.tsx` from
 * anywhere the entry reaches undoes the split, and `scripts/checkBudgets.ts` refuses that by name.
 *
 * The module declares no IndexedDB store: the message tail is core's model database (spec 12 §2.2),
 * the drafts and the outbox core's never-lose-work database (core/drafts/db.ts).
 */

import { lazy } from "react"
import type { FrontendModule } from "../../core/modules/registry.ts"
import type {
	ConversationNotifications,
	ConversationOverride,
	NotifyLevel,
} from "../../core/notify/index.ts"
import { client } from "../../core/store/store.ts"
import { bindChat } from "./bind.ts"
import { ConversationDetailsView } from "./conversation/ConversationDetails.tsx"
import { FILES_VIEW, FilesListView } from "./conversation/FilesList.tsx"
import {
	aboutAConversation,
	CONVERSATION_DETAILS_GROUP,
	PINNED_VIEW,
	PinnedListView,
} from "./conversation/PinnedList.tsx"
import { catchUpSection } from "./home/CatchUpSection.tsx"
import { useChatPresence } from "./presence.ts"
import { chatQuickSwitcher } from "./quickSwitcher.ts"
import { KIND_MESSAGE, SearchHit } from "./search/SearchHit.tsx"
import { chatShortcuts } from "./shortcuts.ts"
import { updateSelf } from "./sidebar/api.ts"
import { requestBrowseChannels } from "./sidebar/browseChannels.ts"
import {
	ChannelActions,
	ChannelDoor,
	ChannelRows,
	channelsEmpty,
	channelsHidden,
	DirectMessageActions,
	DirectMessageRows,
	directsEmpty,
	directsHidden,
	starredEmpty,
	starredHidden,
	StarredRows,
} from "./sidebar/ChatSidebar.tsx"
import { onChatBootstrap } from "./state/bootstrap.ts"
import { chatReducer } from "./state/reducer.ts"
import { chatSlice, defaultNotifyLevel, isMuted } from "./state/selectors.ts"
import { dehydrateChatSlice, hydrateChatSlice, type ChatSlice } from "./state/slice.ts"
import { THREAD_VIEW } from "./thread/openThread.ts"
import { ThreadPlace, ThreadView } from "./thread/ThreadPane.tsx"

/*
 * What Settings ▸ Notifications lists under "Per conversation". Core may not read inside a module's
 * slice of the model or name a module's route (architecture.md D21), so chat hands it the two
 * functions that answer from here: which conversations this person has muted or quietened, and how
 * to put one back. The write is the sidebar's own `PATCH …/self`, so the column has one writer.
 *
 * "Quietened" is measured against the level the conversation itself hands out, never against "every
 * message": a channel is given *Only mentions* when somebody joins it, so a screen that called that
 * an override would list every channel as something this person had switched off, and its *Put it
 * back* would move them off the default instead of onto it (`defaultNotifyLevel`).
 */
const conversationNotifications: ConversationNotifications = {
	overrides: (model) => {
		const now = Date.now()
		const rows: ConversationOverride[] = []
		for (const [key, self] of Object.entries(chatSlice(model).self)) {
			const conv = Number(key)
			const levelChanged = self.notifyLevel !== defaultNotifyLevel(model, conv)
			if (!levelChanged && !isMuted(self, now)) continue
			rows.push({
				conv,
				level: self.notifyLevel as NotifyLevel,
				levelChanged,
				mutedUntil: self.mutedUntil,
			})
		}
		return rows
	},
	reset: async (conv, what) => {
		if (what === "mute") {
			await updateSelf(conv, { mutedUntil: 0 })
			return
		}
		await updateSelf(conv, { notifyLevel: defaultNotifyLevel(client.api.getState(), conv) })
	},
}

/**
 * Administration → Open to AI chats: the channels part, the channels opened to AI chats. A chunk of its
 * own, fetched when an administrator opens that page.
 */
const AgentReachChannels = lazy(() => import("./admin/AgentReachChannels.tsx"))

export const chat: FrontendModule = {
	id: "chat",
	routes: (parent) => import("./routes.tsx").then((chunk) => chunk.loadChatRoutes(parent)),
	reducer: chatReducer,
	provides: {
		// Who is here right now. Presence is chat's own ephemeral (protocol.md §2.5.4) and lands in
		// this module's slice; the surfaces that draw a person are core's and may not read inside
		// that slice (architecture.md D21), so core owns the reader and this module fills it.
		presence: useChatPresence,
		conversationNotifications,
	},
	bind: bindChat,
	slots: {
		sidebarSections: [
			// Above the two kinds, and with no controls of its own: what is in it was put there from the
			// row's own menu in the sidebar (`sidebar/ConversationMenu.tsx`). It answers `isEmpty` and
			// has no `Actions`, which is what makes the section itself come and go with the first star
			// and the last (core/shell/Sidebar.tsx).
			{
				id: "chat.starred",
				titleKey: "chat.sidebar.starred",
				order: 5,
				Rows: StarredRows,
				hiddenCount: starredHidden,
				isEmpty: starredEmpty,
			},
			{
				id: "chat.channels",
				titleKey: "chat.sidebar.channels",
				order: 10,
				Rows: ChannelRows,
				Actions: ChannelActions,
				hiddenCount: channelsHidden,
				isEmpty: channelsEmpty,
				// The column's one door when every section is empty: the first channel of an instance.
				Door: ChannelDoor,
			},
			{
				id: "chat.dms",
				titleKey: "chat.sidebar.directMessages",
				order: 20,
				Rows: DirectMessageRows,
				Actions: DirectMessageActions,
				hiddenCount: directsHidden,
				isEmpty: directsEmpty,
			},
		],
		homeActions: [
			{
				id: "chat.browse",
				labelKey: "chat.sidebar.browse",
				onSelect: requestBrowseChannels,
				primary: true,
			},
		],
		homeSections: [catchUpSection],
		detailsViews: [
			{
				id: THREAD_VIEW,
				titleKey: "chat.thread.title",
				View: ThreadView,
				Aside: ThreadPlace,
			},
			{
				id: "chat.conversation",
				titleKey: "chat.header.detailsTitle",
				groupTitleKey: "chat.header.detailsTitle",
				tabLabelKey: "chat.header.detailsTab",
				View: ConversationDetailsView,
				group: CONVERSATION_DETAILS_GROUP,
				order: 10,
				relevant: aboutAConversation,
			},
			{
				id: FILES_VIEW,
				titleKey: "chat.files.title",
				groupTitleKey: "chat.header.detailsTitle",
				tabLabelKey: "chat.files.tab",
				View: FilesListView,
				group: CONVERSATION_DETAILS_GROUP,
				order: 20,
				relevant: aboutAConversation,
			},
			{
				id: PINNED_VIEW,
				titleKey: "chat.pinned.title",
				groupTitleKey: "chat.header.detailsTitle",
				tabLabelKey: "chat.pinned.tab",
				View: PinnedListView,
				group: CONVERSATION_DETAILS_GROUP,
				order: 30,
				relevant: aboutAConversation,
			},
		],
		agentReach: [{ id: "chat.channels", order: 20, Part: AgentReachChannels }],
		quickSwitcherProviders: [chatQuickSwitcher],
		searchResultRenderers: { [KIND_MESSAGE]: SearchHit },
		shortcuts: chatShortcuts,
	},
	i18n: () => import("./i18n/en.ts").then((chunk) => chunk.en),
	onBootstrap: onChatBootstrap,
	// The warm boot's counterpart: a slice off the disk is this module's empty slice with the cache
	// copied onto it, so a field added since the write arrives empty and the visit's own anchor does
	// not come back (state/slice.ts).
	hydrate: hydrateChatSlice,
	// And what goes to the disk in the first place: the account's own rows, not this second's typing
	// and presence nor the thread windows a pane refetches anyway (state/slice.ts). The cast is the
	// same one `state/selectors.ts` makes — core holds a module's slice as `unknown`, and this file
	// is the module's own wiring, which knows what it is.
	dehydrate: (slice) => dehydrateChatSlice(slice as ChatSlice),
}

export default chat
